import React, { useEffect, useState } from "react";
import { NavLink, Navigate, useLocation, useNavigate } from "react-router-dom";
import { SyncStatusBadge } from "./components";
import AssistantWidget from "./components/AssistantWidget";
import { api } from "./api";
import { isTelegram } from "./tg";

/* ═══════════════════════════════════════════════════
   NAV v3 — verb-based groups
   ═══════════════════════════════════════════════════ */
interface NavItemV3 {
  path: string;
  label: string;
  icon: string;
  badge?: number;
  requiresDays?: number;
}

interface NavSubGroup {
  subgroup: string;
  collapsed?: boolean;
  items: NavItemV3[];
}

interface NavGroup {
  group: string;
  icon: string;
  items: (NavItemV3 | NavSubGroup)[];
}

function isSubGroup(item: NavItemV3 | NavSubGroup): item is NavSubGroup {
  return "subgroup" in item;
}

function buildNav(lowStock: number, pendingPrint = 0): NavGroup[] {
  return [
    { group: "BÁN HÀNG", icon: "point_of_sale", items: [
      { path: "/ops/pos", label: "POS", icon: "point_of_sale" },
    ]},
    { group: "HÔM NAY", icon: "today", items: [
      { path: "/dashboard", label: "Tổng quan", icon: "dashboard" },
      { path: "/finance/pnl", label: "Lãi / Lỗ", icon: "analytics" },
    ]},
    { group: "THEO DÕI", icon: "visibility", items: [
      { path: "/orders", label: "Đơn hàng", icon: "receipt_long" },
      { path: "/sales-history", label: "Lịch sử bán", icon: "history" },
    ]},
    { group: "NHẬP & TỒN", icon: "warehouse", items: [
      { path: "/products", label: "Sản phẩm", icon: "inventory_2" },
      { path: "/inventory", label: "Tồn kho", icon: "monitoring", badge: lowStock > 0 ? lowStock : undefined },
      { path: "/warehouse/import-vn", label: "Nhập hàng", icon: "local_shipping" },
      { path: "/inventory/take", label: "Kiểm kê", icon: "fact_check" },
      { path: "/warehouse/consignment", label: "Hàng ký gửi", icon: "handshake" },
      { path: "/warehouse/materials", label: "Nguyên liệu", icon: "science" },
      { subgroup: "Nâng cao", collapsed: true, items: [
        { path: "/warehouse/backfill", label: "Điều chỉnh", icon: "tune" },
        { path: "/inventory/predictive-restock", label: "Dự đoán", icon: "psychology", requiresDays: 30 },
        { path: "/inventory/abc-analysis", label: "Phân tích ABC", icon: "analytics", requiresDays: 30 },
      ]},
    ]},
    { group: "ĐỐI SOÁT NCC", icon: "storefront", items: [
      { path: "/finance/suppliers", label: "Nhà cung cấp", icon: "store" },
      { path: "/finance/payables", label: "Công nợ", icon: "account_balance" },
      { path: "/finance/expenses", label: "Chi phí", icon: "payments" },
    ]},
    { group: "THUẾ", icon: "receipt_long", items: [
      { path: "/tax/center", label: "Trung tâm Thuế", icon: "account_balance" },
      { path: "/warehouse/virtual", label: "Kho ảo", icon: "category" },
      { path: "/tax/imported-view", label: "Hàng đã nhập (thuế)", icon: "fact_check" },
      { path: "/tax/report", label: "Sổ S1a", icon: "description" },
    ]},
    { group: "VẬN HÀNH", icon: "engineering", items: [
      { path: "/ops/barcode", label: "In tem barcode", icon: "qr_code_2", badge: pendingPrint > 0 ? pendingPrint : undefined },
      { path: "/ops/todos", label: "Việc cần làm", icon: "checklist" },
      { path: "/ops/bank-notifications", label: "Thông báo NH", icon: "notifications" },
      { path: "/ops/assets", label: "Tài sản", icon: "business_center" },
      { subgroup: "Hệ thống", collapsed: true, items: [
        { path: "/ops/backup", label: "Sao lưu", icon: "cloud_upload" },
        { path: "/ops/ai-config", label: "Cấu hình AI", icon: "smart_toy" },
        { path: "/ops/admin", label: "Quản trị", icon: "admin_panel_settings" },
      ]},
    ]},
  ];
}

const PAGE_DESCRIPTIONS: Record<string, string> = {
  "/dashboard": "Tình hình nhanh: KPI, đơn gần nhất, top SP, giờ cao điểm (không lọc Từ/Đến).",
  "/orders": "Tra cứu đơn theo khoảng ngày, kênh thanh toán, tổng tiền.",
  "/sales-history": "Lịch sử bán theo dòng hàng (dữ liệu import từ Google Sheet).",
  "/products": "Master sản phẩm + biến thể + giá vốn / giá bán đồng bộ từ Loyverse.",
  "/inventory": "Tồn kho real-time, cảnh báo hàng sắp hết.",
  "/finance/expenses": "Quản lý chi phí: thêm, lọc theo nhóm, theo dõi tổng chi.",
  "/finance/payables": "Công nợ nhà cung cấp: theo dõi đã trả / còn nợ / quá hạn.",
  "/tax/report": "Sổ S1a — báo cáo bán hàng theo quý, in được.",
  "/tax/center": "Trung tâm Thuế — gom báo cáo & chứng từ khai thuế.",
  "/warehouse/virtual": "Kho ảo: sản phẩm có VAT / Bảng kê.",
  "/warehouse/import": "Nhập hàng: tiểu ngạch, bảng kê 01/TNDN và hóa đơn VAT.",
  "/warehouse/import-vn": "Nhập hàng: tiểu ngạch, bảng kê 01/TNDN và hóa đơn VAT.",
  "/tax/imported-view": "Xem hàng đã nhập (VN/bảng kê/VAT) để đối chiếu khai thuế.",
  "/inventory/take": "Kiểm kê tồn kho: so sánh D1 vs thực tế.",
  "/ops/pos": "Bán hàng tại quầy trên web.",
  "/ops/barcode": "In tem barcode cho sản phẩm (Code128, Tomy 140).",
  "/ops/todos": "Danh sách việc vận hành: đóng gói, giao hàng, nhập kho, gọi NCC — theo dõi tiến độ.",
  "/ops/bank-notifications": "Thông báo ngân hàng bắt được từ app trên máy POS — chỉ xem, không đối soát tự động.",
  "/ops/admin": "Quản trị hệ thống: đồng bộ Loyverse, thống kê, cache, xác thực admin.",
  "/tax/purchase-ledger": "Bảng kê 01/TNDN — mua hàng không hóa đơn theo NĐ 126/2020.",
  "/finance/pnl": "Báo cáo Lãi / Lỗ: doanh thu, giá vốn, chi phí, lãi ròng theo kỳ.",
  "/finance/revenue": "Báo cáo doanh thu chi tiết theo ngày, xuất CSV, so sánh % tăng trưởng.",
  "/finance/suppliers": "Đánh giá nhà cung cấp: điểm giao hàng, chất lượng, giá cả — xếp hạng tổng hợp.",
  "/ops/backup": "Sao lưu dữ liệu D1: tải JSON, backup lên Google Sheet, lịch sử backup.",
  "/ops/assets": "Quản lý tài sản cố định: máy móc, thiết bị, nội thất — theo dõi khấu hao.",
  "/ops/ai-config": "Cấu hình AI provider: Gemini, OpenAI, Anthropic, NVIDIA NIM, Ollama — dịch thuật, gợi ý giá.",
  "/warehouse/consignment": "Hàng ký gửi: gán/gỡ SP cho NCC và đối soát kỳ (đã bán, doanh thu, tiền phải trả NCC) trên cùng trang.",
  "/warehouse/materials": "Nguyên liệu: tồn kho, giá FIFO, stock lots chi tiết.",
  "/warehouse/allocation": "Phân loại tồn kho: chia inventory theo nguồn gốc (mình mua, ký gửi, demo, lỗi).",
  "/warehouse/backfill": "Điều chỉnh tồn kho: ghi nhận lý do chênh lệch (hỏng/mất/trả lại), phê duyệt điều chỉnh.",
  "/inventory/alerts": "Dự đoán hết hàng dựa trên tốc độ bán 30 ngày qua, gợi ý số lượng cần đặt thêm.",
  "/inventory/abc-analysis": "Phân loại sản phẩm theo doanh thu (Pareto 80/20). A=top 70% doanh thu, B=70-90%, C=90-100%.",
  "/inventory/predictive-restock": "Đề xuất Reorder Point và số lượng cần đặt thêm dựa trên tốc độ bán + thời gian giao của NCC + tồn kho an toàn.",
};

const PAGE_TITLES: Record<string, string> = {
  "/dashboard": "Tổng quan",
  "/orders": "Đơn hàng",
  "/sales-history": "Lịch sử bán",
  "/products": "Sản phẩm & Giá bán",
  "/inventory": "Tồn kho",
  "/finance/expenses": "Chi phí",
  "/finance/payables": "Công nợ NCC",
  "/tax/report": "Sổ S1a",
  "/tax/center": "Trung tâm Thuế",
  "/warehouse/virtual": "Kho ảo (VAT / Bảng kê)",
  "/warehouse/import": "Nhập hàng",
  "/warehouse/import-vn": "Nhập hàng",
  "/tax/imported-view": "Hàng đã nhập (thuế)",
  "/inventory/take": "Kiểm kê",
  "/ops/pos": "POS",
  "/ops/barcode": "In tem barcode",
  "/ops/todos": "Việc cần làm",
  "/ops/bank-notifications": "Thông báo ngân hàng",
  "/ops/admin": "Quản trị hệ thống",
  "/tax/purchase-ledger": "Sổ mua hàng (01/TNDN)",
  "/finance/pnl": "Lãi / Lỗ",
  "/finance/revenue": "Báo cáo doanh thu",
  "/finance/suppliers": "Nhà cung cấp",
  "/ops/backup": "Sao lưu dữ liệu",
  "/ops/assets": "Tài sản cố định",
  "/ops/ai-config": "Cấu hình AI",
  "/warehouse/consignment": "Hàng ký gửi",
  "/warehouse/materials": "Nguyên liệu",
  "/warehouse/allocation": "Phân loại tồn kho",
  "/warehouse/backfill": "Điều chỉnh tồn kho",
  "/inventory/alerts": "Cảnh báo tồn kho thông minh",
  "/inventory/abc-analysis": "Phân tích ABC",
  "/inventory/predictive-restock": "Dự đoán đặt hàng",
};

function Sidebar({ lowStock, pendingPrint, open, onClose }: { lowStock: number; pendingPrint: number; open: boolean; onClose: () => void }) {
  const location = useLocation();
  const nav = buildNav(lowStock, pendingPrint);

  // Determine which group contains the active path
  const findActiveGroup = (): string | undefined => {
    for (const g of nav) {
      for (const item of g.items) {
        if (isSubGroup(item)) {
          if (item.items.some(i => location.pathname === i.path)) return g.group;
        } else {
          if (location.pathname === item.path) return g.group;
        }
      }
    }
    return undefined;
  };

  const activeGroup = findActiveGroup();

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    const saved = localStorage.getItem("sidebarExpandedGroups_v3");
    if (saved) {
      try { return new Set(JSON.parse(saved)); } catch { /* fallthrough */ }
    }
    return new Set(activeGroup ? [activeGroup] : ["HÔM NAY"]);
  });

  const [expandedSubGroups, setExpandedSubGroups] = useState<Set<string>>(() => {
    const saved = localStorage.getItem("sidebarExpandedSub_v3");
    if (saved) {
      try { return new Set(JSON.parse(saved)); } catch { /* fallthrough */ }
    }
    return new Set<string>();
  });

  // Data maturity for requiresDays
  const [dataDays, setDataDays] = useState<number>(999);
  useEffect(() => {
    fetch(`${api.baseUrl}/api/data-maturity`, { headers: api.authHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then((d: any) => setDataDays(d.days_with_receipts ?? 999))
      .catch(() => setDataDays(999));
  }, []);

  // Auto-expand group when navigating
  useEffect(() => {
    if (activeGroup && !expandedGroups.has(activeGroup)) {
      const next = new Set(expandedGroups);
      next.add(activeGroup);
      setExpandedGroups(next);
      localStorage.setItem("sidebarExpandedGroups_v3", JSON.stringify([...next]));
    }
  }, [location.pathname, activeGroup]);

  const toggleGroup = (group: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      localStorage.setItem("sidebarExpandedGroups_v3", JSON.stringify([...next]));
      return next;
    });
  };

  const toggleSubGroup = (name: string) => {
    setExpandedSubGroups(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      localStorage.setItem("sidebarExpandedSub_v3", JSON.stringify([...next]));
      return next;
    });
  };

  const renderNavItem = (item: NavItemV3) => {
    const disabled = item.requiresDays != null && dataDays < item.requiresDays;
    return (
      <NavLink
        key={item.path}
        to={disabled ? "#" : item.path}
        end
        className={({ isActive }) =>
          `nav-standalone ${isActive && !disabled ? "active" : ""} ${disabled ? "nav-disabled" : ""}`
        }
        onClick={(e) => { if (disabled) e.preventDefault(); else onClose(); }}
        style={{ position: "relative" }}
      >
        <span className="material-symbols-outlined">{item.icon}</span>
        <span className="nav-label">{item.label}</span>
        {item.badge != null && item.badge > 0 && <span className="nav-badge">{item.badge}</span>}
        {disabled && (
          <span className="nav-tooltip">Cần {item.requiresDays} ngày dữ liệu (có {dataDays})</span>
        )}
      </NavLink>
    );
  };

  return (
    <>
      <div className={`sidebar-backdrop ${open ? "show" : ""}`} onClick={onClose} />
      <aside className={`sidebar ${open ? "open" : ""}`}>
        <div className="brand">
          <h1>Loyverse POS</h1>
          <p>HỆ THỐNG VẬN HÀNH</p>
        </div>

        <nav className="nav">
          {nav.map((group) => {
            const isExpanded = expandedGroups.has(group.group);
            return (
              <div key={group.group} className={`nav-group ${isExpanded ? "expanded" : ""}`}>
                <button
                  type="button"
                  className="nav-group-label"
                  onClick={() => toggleGroup(group.group)}
                  aria-expanded={isExpanded}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{group.icon}</span>
                  <span style={{ flex: 1, textAlign: "left" }}>{group.group}</span>
                  <span className="material-symbols-outlined nav-group-caret">chevron_right</span>
                </button>
                <div className="nav-group-items">
                  {group.items.map((item, idx) => {
                    if (isSubGroup(item)) {
                      const subExpanded = expandedSubGroups.has(item.subgroup);
                      return (
                        <div key={item.subgroup} className={`nav-subgroup ${subExpanded ? "expanded" : ""}`}>
                          <button
                            type="button"
                            className="nav-subgroup-label"
                            onClick={() => toggleSubGroup(item.subgroup)}
                            aria-expanded={subExpanded}
                          >
                            <span style={{ flex: 1, textAlign: "left" }}>{item.subgroup}</span>
                            <span className="material-symbols-outlined nav-subgroup-caret">chevron_right</span>
                          </button>
                          <div className="nav-subgroup-items">
                            {item.items.map(renderNavItem)}
                          </div>
                        </div>
                      );
                    }
                    return renderNavItem(item);
                  })}
                </div>
              </div>
            );
          })}
        </nav>

        <div className="side-footer">
          <div className="side-user">
            <div className="side-user-avatar">L</div>
            <div className="side-user-info">
              <div className="side-user-name">Loyverse Sync</div>
              <div className="side-user-role">đồng bộ mỗi 5 phút</div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}

function Topbar({ onMenuToggle }: { onMenuToggle: () => void }) {
  const openCmdK = () => {
    (window as any).__openCommandPalette?.();
  };

  const logout = async () => {
    await api.logout();
    window.location.href = "/login";
  };

  const handleMenuClick = (e: React.SyntheticEvent) => {
    const now = Date.now();
    if ((window as any).__lastMenuToggle && now - (window as any).__lastMenuToggle < 300) return;
    (window as any).__lastMenuToggle = now;
    onMenuToggle();
  };

  return (
    <header className="topbar">
      <button
        className="topbar-mobile-toggle"
        type="button"
        onClick={handleMenuClick}
        onTouchEnd={(e) => { e.preventDefault(); handleMenuClick(e); }}
        aria-label="Menu"
      >
        <span className="material-symbols-outlined">menu</span>
      </button>
      <div className="search" onClick={openCmdK} style={{ cursor: "pointer" }}>
        <span className="material-symbols-outlined">search</span>
        <input
          placeholder="Tìm trang, sản phẩm..."
          readOnly
          style={{ cursor: "pointer" }}
          onFocus={(e) => { e.target.blur(); openCmdK(); }}
        />
        <span className="search-shortcut">⌘K</span>
      </div>
      <div className="top-actions">
        <SyncStatusBadge />
        <button className="icon-btn" title="Đăng xuất" onClick={logout}>
          <span className="material-symbols-outlined">logout</span>
        </button>
      </div>
    </header>
  );
}

function PageWrapper({ route, children }: { route: string; children: React.ReactNode }) {
  return (
    <div className="page-wrap fade-up">
      <div className="page-header">
        <h2>{PAGE_TITLES[route] || ""}</h2>
        <p className="page-desc">{PAGE_DESCRIPTIONS[route] || ""}</p>
      </div>
      {children}
    </div>
  );
}

function BottomNav({ lowStock, onMoreClick }: { lowStock: number; onMoreClick: () => void }) {
  const bottomItems: { path: string; label: string; icon: string }[] = [
    { path: "/dashboard", label: "Home", icon: "dashboard" },
    { path: "/orders", label: "Đơn", icon: "receipt_long" },
    { path: "/warehouse/import-vn", label: "Nhập", icon: "local_shipping" },
    { path: "/inventory", label: "Kho", icon: "monitoring" },
  ];
  return (
    <nav className="bottom-nav">
      {bottomItems.map((item) => {
        const showBadge = item.path === "/inventory" && lowStock > 0;
        return (
          <NavLink
            key={item.path}
            to={item.path}
            end
            className={({ isActive }) => (isActive ? "active" : "")}
          >
            <span className="bottom-nav-icon">
              <span className="material-symbols-outlined">{item.icon}</span>
              {showBadge && <span className="nav-badge nav-badge-dot">{lowStock}</span>}
            </span>
            <span>{item.label}</span>
          </NavLink>
        );
      })}
      <button className="bottom-nav-more" onClick={onMoreClick}>
        <span className="bottom-nav-icon">
          <span className="material-symbols-outlined">menu</span>
        </span>
        <span>More</span>
      </button>
    </nav>
  );
}

function MobileDrawer({ open, onClose, lowStock, pendingPrint }: { open: boolean; onClose: () => void; lowStock: number; pendingPrint: number }) {
  return (
    <div className={`mobile-drawer-backdrop ${open ? "show" : ""}`} onClick={onClose}>
      <div className="mobile-drawer" onClick={e => e.stopPropagation()}>
        <Sidebar lowStock={lowStock} pendingPrint={pendingPrint} open={false} onClose={onClose} />
      </div>
    </div>
  );
}

export default function App({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const tg = isTelegram();
  const [lowStock, setLowStock] = useState(0);
  const [pendingPrint, setPendingPrint] = useState(0);
  const [loggedIn, setLoggedIn] = useState<boolean | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();

  // Update document.title based on current route
  useEffect(() => {
    const title = PAGE_TITLES[location.pathname] || "Loyverse POS";
    document.title = `${title} · Loyverse POS`;
  }, [location.pathname]);

  // Close sidebar/drawer on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    let alive = true;

    // Check auth with backend — both Telegram and non-Telegram users
    api.me()
      .then(() => alive && setLoggedIn(true))
      .catch((e) => {
        if (!alive) return;
        if (e.status === 401 || e.status === 403) {
          setLoggedIn(false);
          if (!tg && location.pathname !== "/login") {
            navigate("/login", { replace: true });
          }
        } else {
          setLoggedIn(true); // non-auth errors, assume ok to avoid lockout
        }
      });

    // Đếm todo "Chờ in tem" pending → badge trên nav "In tem barcode".
    const refreshPrint = () => api.ops.todos.list({ category: "print", status: "pending" })
      .then((r: any) => alive && setPendingPrint(Array.isArray(r?.todos) ? r.todos.length : (Number(r?.total) || 0)))
      .catch(() => {});
    const refreshBadges = () => {
      if (document.hidden) return;
      api.inventoryBadge()
        .then((s) => alive && setLowStock((s.out_of_stock || 0) + (s.low_stock || 0)))
        .catch(() => {});
      refreshPrint();
    };
    refreshBadges();
    document.addEventListener("visibilitychange", refreshBadges);
    // Badge is informational only → 5 min ceiling; inventory page keeps its own 30s stats refresh.
    const t = setInterval(refreshBadges, 5 * 60_000);
    return () => {
      alive = false;
      clearInterval(t);
      document.removeEventListener("visibilitychange", refreshBadges);
    };
  }, []);

  // Skeleton while checking auth — prevents redirect race
  if (loggedIn === null) {
    return (
      <div className="login-wrap">
        <div className="login-card skeleton-wrap" style={{ width: 320, padding: 40 }}>
          <div className="skeleton-line" style={{ height: 24, marginBottom: 16 }} />
          <div className="skeleton-line" style={{ height: 12 }} />
          <div className="skeleton-line" style={{ height: 12 }} />
          <div className="skeleton-line" style={{ height: 12 }} />
        </div>
      </div>
    );
  }

  // Redirect to login if auth check completed and user is not authenticated
  if (loggedIn === false) {
    if (tg) {
      // Telegram user not authorized — show block screen instead of login
      return (
        <div className="login-wrap">
          <div className="login-card" style={{ textAlign: "center", padding: 40, maxWidth: 360 }}>
            <span className="material-symbols-rounded" style={{ fontSize: 48, color: "#ef4444", marginBottom: 12 }}>block</span>
            <h2 style={{ margin: "0 0 8px", fontSize: 18 }}>Không có quyền truy cập</h2>
            <p style={{ color: "#888", fontSize: 14, margin: 0 }}>
              Tài khoản Telegram của bạn chưa được cấp quyền sử dụng ứng dụng này.
              Vui lòng liên hệ admin để được thêm vào danh sách cho phép.
            </p>
          </div>
        </div>
      );
    }
    return <Navigate to="/login" replace />;
  }

  return (
    <div className={`app ${tg ? "app-tg" : ""}`}>
      <Sidebar lowStock={lowStock} pendingPrint={pendingPrint} open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="main">
        <Topbar onMenuToggle={() => setSidebarOpen(true)} />
        <main className="content">
          <PageWrapper route={location.pathname}>{children}</PageWrapper>
        </main>
      </div>
      <BottomNav lowStock={lowStock} onMoreClick={() => setDrawerOpen(true)} />
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} lowStock={lowStock} pendingPrint={pendingPrint} />
      <AssistantWidget />
    </div>
  );
}